<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import Button from 'primevue/button'

import type { ApiRestaurant } from '@/api/types/location'
import { formatWorkTime } from '@/utils/time'

defineProps<Props>()

const emit = defineEmits<{
  confirm: []
}>()

const { t } = useI18n()

/**
 * Карточка филиала: заголовок отдельной плашкой, под ней — время работы,
 * адрес и телефон списком, снизу подтверждение. Плашки разделены полоской
 * фона в 4px, как на фрейме «Филиал».
 */
interface Props {
  branch: ApiRestaurant
  loading?: boolean
}

</script>

<template>
  <div class="branch-card">
    <header class="branch-card__header">
      <h2 class="branch-card__title">{{ branch.name }}</h2>
    </header>

    <div class="branch-card__block">
      <dl class="branch-card__rows">
        <div class="branch-card__row">
          <dt class="branch-card__label font-14-s">{{ t('branch.workTime') }}</dt>
          <dd class="branch-card__value font-16-m">
            {{ formatWorkTime(branch.startOfWorkingTime, branch.endOfWorkingTime) }}
          </dd>
        </div>

        <div class="branch-card__row">
          <dt class="branch-card__label font-14-s">{{ t('branch.address') }}</dt>
          <dd class="branch-card__value font-16-m">{{ branch.address.trim() }}</dd>
        </div>

        <div v-if="branch.pointOfContact" class="branch-card__row">
          <dt class="branch-card__label font-14-s">{{ t('branch.phone') }}</dt>
          <dd class="branch-card__value font-16-m">{{ branch.pointOfContact }}</dd>
        </div>
      </dl>

      <footer class="branch-card__footer">
        <Button
          :label="t('common.confirm')"
          :loading="loading"
          class="branch-card__cta"
          @click="emit('confirm')"
        />
      </footer>
    </div>
  </div>
</template>

<style scoped lang="scss">
.branch-card {
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  background: var(--surface-muted);

  &__header {
    padding: var(--screen-padding);
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
  }

  &__title {
    font-size: 24px;
    font-weight: var(--fw-bold);
    line-height: 37px;
  }

  /* Отделён от заголовка полоской фона в 4px */
  &__block {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    margin-top: 4px;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    background: var(--surface-card);
  }

  &__rows {
    display: flex;
    overflow-y: auto;
    flex-direction: column;
    min-height: 0;
    padding: var(--screen-padding) var(--screen-padding) 0;
  }

  /* Между строками 14px, полоска 1px и снова 14px */
  &__row + &__row {
    margin-top: var(--space-7);
    padding-top: var(--space-7);
    border-top: 1px solid var(--divider-color);
  }

  &__label {
    line-height: 21px;
    color: var(--text-color-secondary);
  }

  &__value {
    margin: 0;
    line-height: 24px;
    color: var(--text-color);
  }

  &__footer {
    padding: var(--screen-padding);
    padding-bottom: calc(29px + var(--tg-safe-area-bottom));
  }

  &__cta {
    width: 100%;
  }
}
</style>
